import {mappedFields, unitFormats} from '@/lib/StandardFields';
import type {MappedField} from '@/lib/StandardFields';
import type {GridData, CellStyleFunc, ValueFormatterFunc} from '@/components/DataTable/types';

export interface GridConfigOptions {
  title: string;
  mappingKey: string;
  /** Columns hidden by default. Ignored when `visibleColumns` is set. */
  hiddenColumns?: string[];
  /** Fields excluded from the chart-picker. Ignored when `visibleColumns` is set. */
  hiddenFields?: string[];
  /**
   * Allowlist of column keys to show. When provided, anything not in this list
   * is hidden in both the column view and the chart-picker. Convenient when the
   * source has many columns and only a few should be visible.
   */
  visibleColumns?: string[];
  subtitle?: (record: Record<string, unknown>) => string;
  /**
   * When true, column headers render as the field's `label` plus a parenthesized
   * unit suffix (e.g. `Total Upgrade Cost (MM)`), and value cells skip the unit
   * suffix (the prefix like `$` stays on the value). Set for sources whose raw
   * column names are not user-friendly (e.g. UPPER_SNAKE_CASE Snowflake columns).
   */
  useFieldLabelHeaders?: boolean;
}

export function getGenericGridData(
  itemsPromise: Promise<Record<string, unknown>[]>,
  options: GridConfigOptions,
): GridData {
  const fields = mappedFields[options.mappingKey];
  const isHiddenColumn = (key: string): boolean => options.visibleColumns
    ? !options.visibleColumns.includes(key)
    : (options.hiddenColumns ?? []).includes(key);

  const columnsPromise = itemsPromise.then((items) => {
    const item = items?.[0];
    if (!item) return [];
    return Object.keys(item).map((key) => {
      const field = fields.find((f) => f.bqHeader === key);
      const headerName = getColHeaderName(key, field, options.useFieldLabelHeaders);
      return {
        headerName,
        field: key,
        sortable: true,
        filter: getColFilterType(key, items),
        filterParams: {buttons: ['reset']},
        resizable: true,
        hide: isHiddenColumn(key),
        headerTooltip: headerName,
        cellStyle: genericCellStyle,
        valueFormatter: getGenericValueFormatter(field, options.useFieldLabelHeaders),
      };
    });
  });

  const fieldOptions = columnsPromise.then((columns) => {
    return columns
      .filter((column) => {
        const headerName = Array.isArray(column?.headerName) ? column.headerName[0] : column?.headerName;
        if (!headerName) return false;
        if (options.visibleColumns) {
          return !!column.field && options.visibleColumns.includes(column.field);
        }
        const hiddenFields = options.hiddenFields ?? [];
        const isHiddenByName = hiddenFields.includes(headerName);
        const isHiddenByField = !!column.field && hiddenFields.includes(column.field);
        return !isHiddenByName && !isHiddenByField;
      })
      .map((column) => {
        const headerName = Array.isArray(column?.headerName) ? column.headerName[0] : column?.headerName;
        let value: string | number = headerName;
        // When the unit is baked into the displayed header, a header-string
        // lookup never matches. Fall back to the raw column key (which equals
        // `f.bqHeader`) instead.
        const field = options.useFieldLabelHeaders
          ? fields.find((f) => f.bqHeader === column.field)
          : fields.find((f) => f.bqHeader === value || f.label === value);
        if (!field) {
          return {
            value,
            unitFormat: null,
            formattedTitle: value,
            formattedValue: value,
          };
        }

        const unitFormat = field.formatUnit && unitFormats[field.formatUnit];
        if (typeof value === 'number') {
          value = value.toLocaleString('en-US');
        }

        // When the unit is already in the header, don't re-append it.
        let formattedTitle = value;
        if (!options.useFieldLabelHeaders && unitFormat) {
          formattedTitle = `${value} ` + `(${unitFormat?.prefix ?? ''}${unitFormat?.suffix ?? ''})`.replace(/\s/g, '');
        }

        return {
          value,
          unitFormat,
          formattedTitle,
        };
      });
  });

  return {
    items: itemsPromise,
    config: {
      showRowNumbers: true,
      hiddenColumns: options.hiddenColumns ?? [],
      title: options.title,
      subtitle: options.subtitle,
      columns: columnsPromise,
      fieldOptions,
      chartTypes: ['line', 'bar', 'area', 'pie'],
    },
  };
}

const genericCellStyle: CellStyleFunc = (params) => {
  const style: Record<string, string> = {color: 'black'};
  if (typeof params.value === 'number' && params.value < 0) {
    style.color = 'red';
  }
  return style;
};

function getGenericValueFormatter(field: MappedField | undefined, unitsInHeader = false): ValueFormatterFunc {
  const unitFormat = field?.formatUnit && unitFormats?.[field.formatUnit];
  return (params) => {
    let value = params.value;
    if (value == null) {
      return 'N/A';
    }
    if (typeof value === 'number') {
      value = value.toLocaleString('en-US');
    }
    if (unitsInHeader) {
      // Suffix lives in the header; keep the prefix (e.g. `$`) on the value as a
      // visual cue for currency-style columns.
      return `${unitFormat?.prefix ?? ''}${value}`;
    }
    return `${unitFormat?.prefix ?? ''}${value}${unitFormat?.suffix ?? ''}`;
  };
}

function getColFilterType(key: string, items: Record<string, unknown>[]) {
  // Find a non-null value to determine the type, otherwise the first occurrence
  // would be used and may not be the correct type.
  const nonNullValue = items.find((item) => item[key] !== null);

  if (!nonNullValue) {
    return 'agTextColumnFilter';
  }

  const type = typeof nonNullValue[key];

  switch (type) {
    case 'number': return 'agNumberColumnFilter';
    case 'string': return 'agTextColumnFilter';
    case 'boolean': return 'agTextColumnFilter'; // Set Filter is Enterprise.
    default: return 'agTextColumnFilter';
  }
}

/**
 * Builds a parenthesized unit suffix from a field's `formatUnit`, e.g.
 * `{suffix: ' MM'}` -> ` (MM)`. Only the suffix is used — prefixes (like `$`)
 * stay on the value as a visual cue, so the header doesn't double up on them.
 * Returns '' when the field has no format unit suffix.
 */
function getUnitHeaderSuffix(field: MappedField | undefined): string {
  if (!field?.formatUnit) return '';
  const unitFormat = unitFormats[field.formatUnit];
  if (!unitFormat?.suffix) return '';
  const trimmed = unitFormat.suffix.trim();
  return trimmed ? ` (${trimmed})` : '';
}

function getColHeaderName(key: string, field: MappedField | undefined, useFieldLabelHeaders = false): string {
  const replace = ['Abs_x_UC', 'Abs_x_Vac', 'UC_x_Lease_Risk'];
  if (!useFieldLabelHeaders && !replace.includes(key)) return key;
  const label = field?.label || key;
  const labelStr = Array.isArray(label) ? label.join(', ') : label;
  return useFieldLabelHeaders ? `${labelStr}${getUnitHeaderSuffix(field)}` : labelStr;
}
